// Pull a numeric total out of the opaque `PreviewItem.price` jsonb +
// `PreviewItem.travellers` jsonb so a single formatted currency value can
// be rendered. Shared so both the preview FE (optional-price toggle) and
// the preview API (optional-alternative price difference) read the SAME
// figure — they must agree or the on-screen optional price and the
// "Difference" line would contradict each other.
//
// Mirrors the legacy `getProductPrice` (travel_planner_price_module):
//   - rooms[*]: sum `room.price * (room.units ?? 1) * roomNights`
//     (pricePeriods if present override). roomNights derives from the
//     item's start/end date — defaults to 1 when missing.
//   - travellers[*]: sum `traveller.price * itemTravellerQuantity` where
//     quantity is the count of matching `travelerType.id` rows on the
//     item.
//   - flat `price`: `price * (pricingUnits ?? 1)`.
//
// Legacy parity: when no traveller match is found the multiplier
// defaults to 1 (preserves the standalone `price` field).
const isObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object';

const num = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const parsed = Number(v);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

// Item.travellers can arrive as either the raw DB jsonb
// `{ people: [{ travelerTypeId, title }] }` (one row per person, count
// is the row count) or the legacy transformed shape
// `[{ quantity, travelerType: { id } }]`. Both surface here as
// `unknown` — return a `travelerTypeId → quantity` map.
const buildTravellerCounts = (raw: unknown): Map<string, number> => {
  const counts = new Map<string, number>();
  if (Array.isArray(raw)) {
    // Transformed shape.
    for (const entry of raw) {
      if (!isObject(entry)) continue;
      const q = num(entry.quantity) ?? 0;
      const tt = entry.travelerType;
      const id = isObject(tt) && typeof tt.id === 'string' ? tt.id : null;
      if (!id || q <= 0) continue;
      counts.set(id, (counts.get(id) ?? 0) + q);
    }
    return counts;
  }
  if (isObject(raw)) {
    const people = raw.people;
    if (Array.isArray(people)) {
      for (const person of people) {
        if (!isObject(person)) continue;
        const id =
          typeof person.travelerTypeId === 'string'
            ? person.travelerTypeId
            : null;
        if (!id) continue;
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
    }
  }
  return counts;
};

const computeRoomNights = (
  startTime: string | null,
  endTime: string | null
): number => {
  if (!startTime || !endTime) return 1;
  const a = new Date(startTime).getTime();
  const b = new Date(endTime).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return 1;
  const days = Math.round((b - a) / (1000 * 60 * 60 * 24));
  return Math.max(1, days);
};

const sumRooms = (rooms: unknown, roomNights: number): number | null => {
  if (!Array.isArray(rooms)) return null;
  let total = 0;
  let hit = false;
  for (const room of rooms) {
    if (!isObject(room)) continue;
    const units = num(room.units) ?? 1;
    const periods = Array.isArray(room.pricePeriods) ? room.pricePeriods : null;
    if (periods && periods.length > 0) {
      let periodsTotal = 0;
      let periodHit = false;
      for (const p of periods) {
        if (!isObject(p)) continue;
        const price = num(p.price) ?? num(p.netCost) ?? null;
        if (price === null) continue;
        const nights = num(p.nightCount) ?? 1;
        periodsTotal += price * nights;
        periodHit = true;
      }
      if (periodHit) {
        total += periodsTotal * units;
        hit = true;
      }
      continue;
    }
    const price = num(room.price) ?? num(room.netCost) ?? null;
    if (price === null) continue;
    total += price * units * roomNights;
    hit = true;
  }
  return hit ? total : null;
};

const sumTravellers = (
  travellers: unknown,
  counts: Map<string, number>
): number | null => {
  if (!Array.isArray(travellers)) return null;
  let total = 0;
  let hit = false;
  for (const t of travellers) {
    if (!isObject(t)) continue;
    const price = num(t.price) ?? num(t.netCost) ?? null;
    if (price === null) continue;
    const id = typeof t.travellerId === 'string' ? t.travellerId : null;
    const quantity = id ? (counts.get(id) ?? 1) : 1;
    total += price * quantity;
    hit = true;
  }
  return hit ? total : null;
};

export type ExtractPriceOptions = {
  travellers?: unknown;
  startTime?: string | null;
  endTime?: string | null;
  pricingUnits?: number | null;
};

export const extractPriceTotal = (
  raw: unknown,
  options: ExtractPriceOptions = {}
): number | null => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    // `Number('')` / `Number('   ')` coerce to 0 — treat a blank string
    // as "no price" so callers fall through to their fallback path
    // instead of rendering a bogus 0.
    if (raw.trim() === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (!isObject(raw)) return null;

  const roomNights = computeRoomNights(
    options.startTime ?? null,
    options.endTime ?? null
  );

  const rooms = sumRooms(raw.rooms, roomNights);
  if (rooms !== null) return rooms;

  const counts = buildTravellerCounts(options.travellers);
  const travellers = sumTravellers(raw.travellers, counts);
  if (travellers !== null) return travellers;

  // Try a wide set of flat-total keys. Legacy product price jsonb varies
  // by product type — covers `Price`, `ProductPrice`, `PopulatedPrice`,
  // raw cost rows, and a few legacy aliases.
  const flat =
    num(raw.price) ??
    num(raw.total) ??
    num(raw.totalCost) ??
    num(raw.totalPrice) ??
    num(raw.gross) ??
    num(raw.grossPrice) ??
    num(raw.netCost) ??
    num(raw.amount) ??
    num(raw.value) ??
    num(raw.cost);
  if (flat !== null) {
    const units = options.pricingUnits ?? num(raw.pricingUnits) ?? 1;
    return flat * units;
  }

  // Recurse one level when the row wraps a nested price object — covers
  // shapes like `{ data: {...} }`, `{ result: {...} }`, `{ price: {...} }`.
  for (const key of ['data', 'result', 'price', 'pricing'] as const) {
    const nested = raw[key];
    if (isObject(nested)) {
      const inner = extractPriceTotal(nested, options);
      if (inner !== null) return inner;
    }
  }
  return null;
};
